/**
 * Live match updates via Socket.io.
 * Subscribes to per-match rooms (subscribe:matches) so the server only emits match:update to interested clients.
 * Throttles React Query invalidation to avoid refetch storms (all platforms).
 */
import React, { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { io, type Socket } from 'socket.io-client';
import { SOCKET_IO_URL } from '../../../constants/api';
import {
  LIVE_MATCH_UPDATE_EVENT,
  LIVE_MATCH_UPDATE_INVALIDATE_WINDOW_MS,
  LIVE_SUBSCRIPTION_MAX_MATCH_IDS,
  LIVE_SUBSCRIPTION_RESYNC_DEBOUNCE_MS,
  SOCKET_CLIENT_SUBSCRIBE_MATCHES_EVENT,
} from '../constants/liveUpdates.constants';
import { isWorldCupGroupLeaderboardQueryKey } from '../../worldcup/helpers/worldCupLiveLeaderboardInvalidation.helper';
import { isCompetitionLeaderboardQueryKey } from '../../competitions/helpers/competitionLeaderboardInvalidation.helper';
import { collectMatchIdsForLiveSubscriptions } from '../helpers/liveSubscriptionMatchIds.helper';
import { planMatchUpdateThrottle } from '../helpers/matchUpdateThrottle.helper';

export function LiveUpdatesProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const socketRef = useRef<Socket | null>(null);
  const lastFlushAtRef = useRef(0);
  const pendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const subscribeScheduleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSubscribeSigRef = useRef<string>('');

  useEffect(() => {
    const socket = io(SOCKET_IO_URL, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      autoConnect: true,
    });
    socketRef.current = socket;

    const flushInvalidations = () => {
      queryClient.invalidateQueries({ queryKey: ['challenges', 'active'] });
      queryClient.invalidateQueries({ queryKey: ['challenges'] });
      // Every competition's global leaderboard (WC + season pools) — matching by key
      // shape rather than a single hardcoded id, so season standings move live too.
      queryClient.invalidateQueries({
        predicate: (query) => isCompetitionLeaderboardQueryKey(query.queryKey),
      });
      // Squad/group leaderboards (incl. TV mode). Without this, squad standings
      // only refresh on a staleTime-expiry remount, so they don't move live
      // during or after a match.
      queryClient.invalidateQueries({
        predicate: (query) => isWorldCupGroupLeaderboardQueryKey(query.queryKey),
      });
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'groups' && query.queryKey[2] === 'challenges',
      });
      queryClient.invalidateQueries({ queryKey: ['matches'] });
      queryClient.invalidateQueries({ queryKey: ['gameResults'] });
    };

    const onMatchUpdate = (payload?: { matchId?: string; eventsUpdated?: boolean }) => {
      // Targeted events invalidation — bypasses throttle (separate query key)
      if (payload?.eventsUpdated && payload?.matchId) {
        queryClient.invalidateQueries({ queryKey: ['match-events-raw', payload.matchId] });
      }

      const now = Date.now();
      const plan = planMatchUpdateThrottle(
        now,
        lastFlushAtRef.current,
        LIVE_MATCH_UPDATE_INVALIDATE_WINDOW_MS,
      );

      if (plan.flushImmediately) {
        lastFlushAtRef.current = now;
        flushInvalidations();
        if (pendingTimeoutRef.current != null) {
          clearTimeout(pendingTimeoutRef.current);
          pendingTimeoutRef.current = null;
        }
        return;
      }

      if (pendingTimeoutRef.current != null) {
        return;
      }

      const delay = plan.delayMs ?? 0;
      pendingTimeoutRef.current = setTimeout(() => {
        pendingTimeoutRef.current = null;
        lastFlushAtRef.current = Date.now();
        flushInvalidations();
      }, delay);
    };

    const flushSubscribeMatches = () => {
      subscribeScheduleRef.current = null;
      if (!socket.connected) return;
      const ids = collectMatchIdsForLiveSubscriptions(
        queryClient,
        LIVE_SUBSCRIPTION_MAX_MATCH_IDS,
      );
      const sig = [...ids].sort().join('\0');
      if (sig === lastSubscribeSigRef.current) return;
      lastSubscribeSigRef.current = sig;
      socket.emit(SOCKET_CLIENT_SUBSCRIBE_MATCHES_EVENT, { ids });
    };

    const scheduleSubscribeMatches = () => {
      if (subscribeScheduleRef.current != null) {
        clearTimeout(subscribeScheduleRef.current);
      }
      subscribeScheduleRef.current = setTimeout(
        flushSubscribeMatches,
        LIVE_SUBSCRIPTION_RESYNC_DEBOUNCE_MS,
      );
    };

    const onConnect = () => {
      scheduleSubscribeMatches();
    };

    socket.on(LIVE_MATCH_UPDATE_EVENT, onMatchUpdate);
    socket.on('connect', onConnect);
    if (socket.connected) {
      scheduleSubscribeMatches();
    }

    const unsubCache = queryClient.getQueryCache().subscribe(() => {
      if (socket.connected) {
        scheduleSubscribeMatches();
      }
    });

    return () => {
      unsubCache();
      if (pendingTimeoutRef.current != null) {
        clearTimeout(pendingTimeoutRef.current);
        pendingTimeoutRef.current = null;
      }
      if (subscribeScheduleRef.current != null) {
        clearTimeout(subscribeScheduleRef.current);
        subscribeScheduleRef.current = null;
      }
      socket.off(LIVE_MATCH_UPDATE_EVENT, onMatchUpdate);
      socket.off('connect', onConnect);
      socket.disconnect();
      socketRef.current = null;
      lastSubscribeSigRef.current = '';
    };
  }, [queryClient]);

  return <>{children}</>;
}
